import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/MembersGroupUser';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const MEMBERS_GROUP_USER_API_REDUCER_KEY = 'members-group-users';
export const MEMBERS_GROUP_USER_PATH = 'members-group-users';

export const membersGroupUserApi = createApi({
    reducerPath: MEMBERS_GROUP_USER_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['MembersGroupUser', 'MembersGroupUsers'],
    endpoints: (builder) => ({
        getMembersGroupUserList: builder.query<PaginationResult<Entity.MembersGroupUser>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ MEMBERS_GROUP_USER_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['MembersGroupUsers'],
        }),
        getMembersGroupUser: builder.query<Entity.MembersGroupUser, Entity.PrimaryMembersGroupUser>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = membersGroupUserNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ MEMBERS_GROUP_USER_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['MembersGroupUser'],
        }),
        getMembersGroupUserMultiple: builder.query<Entity.MembersGroupUser, Entity.PrimaryMembersGroupUser[]>({
            query: (ids) => {
                return ({
                    url: `/${ MEMBERS_GROUP_USER_PATH }/ids${Entity.PrimaryMembersGroupUser.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['MembersGroupUsers'],
        }),
        getMembersGroupUserPresentation: builder.query<PaginationResult<Entity.MembersGroupUser>, Entity.PresentationMembersGroupUserRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ MEMBERS_GROUP_USER_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['MembersGroupUsers'],
        }),
        getMembersGroupUserAdvancedPresentation: builder.query<PaginationResult<Entity.MembersGroupUser>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ MEMBERS_GROUP_USER_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['MembersGroupUsers'],
        }),
        
        insertMembersGroupUser: builder.mutation<Entity.MembersGroupUser, Entity.CreateMembersGroupUser>({
            query: (toInsert) => {
                return ({
                    url: `/${ MEMBERS_GROUP_USER_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    membersGroupUserApi.util.upsertQueryData('getMembersGroupUser', Entity.PrimaryMembersGroupUser.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['MembersGroupUsers'],
        }),
        insertMultipleMembersGroupUser: builder.mutation<Entity.MembersGroupUser[], Entity.CreateMembersGroupUser[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ MEMBERS_GROUP_USER_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            membersGroupUserApi.util.upsertQueryData('getMembersGroupUser', Entity.PrimaryMembersGroupUser.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['MembersGroupUsers'],
        }),
        
        deleteMembersGroupUser: builder.mutation<Entity.MembersGroupUser, Entity.MembersGroupUser>({
            query: (toDelete) => {
                return ({
                    url: `/${ MEMBERS_GROUP_USER_PATH }/${Entity.PrimaryMembersGroupUser.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['MembersGroupUsers'],
        }),
        deleteMultipleMembersGroupUser: builder.mutation<Entity.MembersGroupUser[], Entity.PrimaryMembersGroupUser[]>({
            query: (ids) => {
                return ({
                    url: `/${ MEMBERS_GROUP_USER_PATH }/batch${Entity.PrimaryMembersGroupUser.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['MembersGroupUsers'],
        }),
    }),
})

export const [membersGroupUserNormalizer, membersGroupUserNormalizerMiddleware] = createNormalizationMiddleware(MEMBERS_GROUP_USER_API_REDUCER_KEY, membersGroupUserApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetMembersGroupUserListQuery,
    useGetMembersGroupUserMultipleQuery,
    useGetMembersGroupUserPresentationQuery,
    useGetMembersGroupUserQuery,
    useInsertMembersGroupUserMutation,
    useInsertMultipleMembersGroupUserMutation,
    
    useDeleteMembersGroupUserMutation,
    useDeleteMultipleMembersGroupUserMutation,
    useGetMembersGroupUserAdvancedPresentationQuery,
    
} = membersGroupUserApi
